'use client';

import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';

// Global error suppression for Supabase auth errors
if (typeof window !== 'undefined') {
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  
  console.error = (...args: any[]) => {
    const message = args[0]?.toString() || '';
    const stack = args[0]?.stack || '';
    
    // Suppress specific Supabase auth errors
    if (
      message.includes('Invalid Refresh Token') ||
      message.includes('Refresh Token Not Found') ||
      message.includes('refresh_token_not_found') ||
      stack.includes('@supabase/auth-js') ||
      stack.includes('GoTrueClient')
    ) {
      // Silently ignore these errors
      return;
    }
    
    originalConsoleError.apply(console, args);
  };
  
  console.warn = (...args: any[]) => {
    const message = args[0]?.toString() || '';
    
    // Allow our own auth warnings but suppress Supabase internal warnings
    if (message.startsWith('[Auth]')) {
      originalConsoleWarn.apply(console, args);
      return;
    }
    
    // Suppress Supabase auth warnings
    if (
      message.includes('Invalid Refresh Token') ||
      message.includes('Refresh Token Not Found') ||
      message.includes('refresh_token_not_found')
    ) {
      return;
    }
    
    originalConsoleWarn.apply(console, args);
  };
}

// Global state management for auth operations
let clientInstance: SupabaseClient | null = null;
let isInitializing = false;
let initPromise: Promise<SupabaseClient> | null = null;

// Request deduplication - prevent multiple simultaneous auth requests
let pendingSessionRequest: Promise<any> | null = null;
let pendingUserRequest: Promise<any> | null = null;

// Storage cleanup tracking
let hasCleanedStorageThisSession = false;
let lastCleanupTime = 0;
const CLEANUP_COOLDOWN_MS = 5000; // 5 seconds cooldown between cleanups
let storageJustCleared = false; // NEW: Track if we just cleared storage

// Rate limiting with exponential backoff
let rateLimitedUntil: number = 0;
let consecutiveErrors = 0;
let lastErrorTime = 0;
const MAX_CONSECUTIVE_ERRORS = 3;
const ERROR_COOLDOWN_MS = 10000; // 10 seconds between error attempts
const RATE_LIMIT_COOLDOWNS = [30000, 60000, 120000, 240000]; // 30s, 1m, 2m, 4m

// Storage validation and cleanup
function clearAuthStorage(): void {
  if (typeof window === 'undefined') return;
  
  // Prevent repeated cleanups within cooldown period
  const now = Date.now();
  if (hasCleanedStorageThisSession && (now - lastCleanupTime) < CLEANUP_COOLDOWN_MS) {
    console.log('[Auth] Cleanup already performed recently, skipping');
    return;
  }
  
  console.log('[Auth] Clearing all authentication storage');
  hasCleanedStorageThisSession = true;
  lastCleanupTime = now;
  storageJustCleared = true; // NEW: Mark that we just cleared storage
  
  try {
    // Clear localStorage
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.includes('supabase') || key.includes('auth')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear cookies
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      cookies.forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        if (name.includes('supabase') || name.includes('auth')) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
    }
    
    // Reset error tracking
    consecutiveErrors = 0;
    rateLimitedUntil = 0;
    lastErrorTime = 0;
    pendingSessionRequest = null;
    pendingUserRequest = null;
    
    // Force client recreation on next access
    clientInstance = null;
  } catch (error) {
    console.error('[Auth] Error clearing storage:', error);
  }
}

// CRITICAL: Proactive token validation - runs BEFORE client creation
function proactiveTokenValidation(): void {
  if (typeof window === 'undefined') return;
  
  try {
    const keys = Object.keys(localStorage);
    const authKeys = keys.filter(key => key.includes('supabase.auth.token'));
    
    let shouldClear = false;
    
    for (const key of authKeys) {
      const value = localStorage.getItem(key);
      if (!value) continue;
      
      try {
        const parsed = JSON.parse(value);
        
        // Check for missing or invalid refresh token
        if (!parsed.refresh_token || 
            typeof parsed.refresh_token !== 'string' || 
            parsed.refresh_token.length < 10) {
          console.warn('[Auth] Invalid refresh token detected, clearing storage');
          shouldClear = true;
          break;
        }
        
        // Check for missing or invalid access token
        if (!parsed.access_token || 
            typeof parsed.access_token !== 'string' || 
            parsed.access_token.length < 20) {
          console.warn('[Auth] Invalid access token detected, clearing storage');
          shouldClear = true;
          break;
        }
        
        // Check for expired sessions (more than 7 days old)
        if (parsed.expires_at) {
          const expiresAt = parsed.expires_at * 1000;
          const now = Date.now();
          const sevenDaysAgo = now - (7 * 24 * 60 * 60 * 1000);
          
          if (expiresAt < sevenDaysAgo) {
            console.warn('[Auth] Expired session detected, clearing storage');
            shouldClear = true;
            break;
          }
        }
        
        // Check for malformed token structure
        if (typeof parsed !== 'object' || Array.isArray(parsed)) {
          console.warn('[Auth] Malformed token structure, clearing storage');
          shouldClear = true;
          break;
        }
      } catch (e) {
        console.warn('[Auth] Failed to parse token, clearing storage');
        shouldClear = true;
        break;
      }
    }
    
    if (shouldClear) {
      clearAuthStorage();
    }
  } catch (error) {
    console.error('[Auth] Proactive validation error:', error);
    clearAuthStorage();
  }
}

// Validate stored session data
function validateStoredSession(): boolean {
  if (typeof window === 'undefined') return true;
  
  try {
    const keys = Object.keys(localStorage);
    const authKeys = keys.filter(key => key.includes('supabase.auth.token'));
    
    for (const key of authKeys) {
      const value = localStorage.getItem(key);
      if (!value) continue;
      
      try {
        const parsed = JSON.parse(value);
        
        // Validate structure
        if (!parsed || typeof parsed !== 'object') {
          console.warn('[Auth] Malformed session data');
          return false;
        }
        
        // Check for expired tokens (more than 1 hour old)
        if (parsed.expires_at) {
          const expiresAt = parsed.expires_at * 1000;
          const now = Date.now();
          
          if (expiresAt < now - (60 * 60 * 1000)) {
            console.warn('[Auth] Stale session detected');
            return false;
          }
        }
        
        // Validate refresh token format
        if (parsed.refresh_token && typeof parsed.refresh_token !== 'string') {
          console.warn('[Auth] Invalid refresh token format');
          return false;
        }
        
        // Check for empty or malformed tokens
        if (parsed.access_token && (typeof parsed.access_token !== 'string' || parsed.access_token.length < 20)) {
          console.warn('[Auth] Invalid access token');
          return false;
        }
      } catch (e) {
        console.warn('[Auth] Failed to parse session data');
        return false;
      }
    }
  } catch (error) {
    console.error('[Auth] Error validating session:', error);
    return false;
  }
  
  return true;
}

// Check if currently rate limited
function isRateLimited(): boolean {
  if (rateLimitedUntil > Date.now()) {
    const remainingSeconds = Math.ceil((rateLimitedUntil - Date.now()) / 1000);
    console.warn(`[Auth] Rate limited. Retry in ${remainingSeconds}s`);
    return true;
  }
  return false;
}

// Set rate limit cooldown with exponential backoff
function setRateLimitCooldown(): void {
  const cooldownIndex = Math.min(consecutiveErrors, RATE_LIMIT_COOLDOWNS.length - 1);
  const cooldownMs = RATE_LIMIT_COOLDOWNS[cooldownIndex];
  rateLimitedUntil = Date.now() + cooldownMs;
  console.warn(`[Auth] Rate limit cooldown: ${cooldownMs / 1000}s`);
}

// Check if should throttle based on recent errors
function shouldThrottleRequest(): boolean {
  const now = Date.now();
  if (now - lastErrorTime < ERROR_COOLDOWN_MS) {
    console.warn('[Auth] Throttling request due to recent errors');
    return true;
  }
  return false;
}

// Handle authentication errors comprehensively
function handleAuthError(error: any, context: string): { data: any; error: null } {
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorCode = error?.code || '';
  const errorName = error?.name || '';
  
  console.warn(`[Auth] ${context} error:`, errorMessage || errorName);
  lastErrorTime = Date.now();
  
  // Handle AbortError
  if (errorName === 'AbortError' || errorMessage.includes('abort')) {
    console.warn('[Auth] Request aborted, clearing storage');
    clearAuthStorage();
    return { data: { session: null, user: null }, error: null };
  }
  
  // Handle network/fetch errors
  if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorMessage.includes('failed to fetch')) {
    console.warn('[Auth] Network error detected');
    consecutiveErrors++;
    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
      clearAuthStorage();
    }
    return { data: { session: null, user: null }, error: null };
  }
  
  // Handle invalid refresh token
  if (errorMessage.includes('refresh_token') || 
      errorMessage.includes('invalid refresh') ||
      errorCode === 'refresh_token_not_found') {
    console.warn('[Auth] Invalid refresh token, clearing storage');
    clearAuthStorage();
    return { data: { session: null, user: null }, error: null };
  }
  
  // Handle rate limiting
  if (errorMessage.includes('rate limit') || 
      errorMessage.includes('too many') ||
      errorCode === 'over_request_rate_limit') {
    console.warn('[Auth] Rate limit detected');
    consecutiveErrors++;
    setRateLimitCooldown();
    
    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
      clearAuthStorage();
    }
    return { data: { session: null, user: null }, error: null };
  }
  
  // Handle other auth errors
  consecutiveErrors++;
  if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
    console.error('[Auth] Too many consecutive errors, clearing storage');
    clearAuthStorage();
  }
  
  return { data: { session: null, user: null }, error: null };
}

// Create wrapped getSession with request deduplication
function createSafeGetSession(originalGetSession: any) {
  return async function safeGetSession() {
    // Return pending request if one exists
    if (pendingSessionRequest) {
      return pendingSessionRequest;
    }
    
    // Check rate limiting
    if (isRateLimited() || shouldThrottleRequest()) {
      return { data: { session: null }, error: null };
    }
    
    // Create new request
    pendingSessionRequest = (async () => {
      try {
        const result = await originalGetSession();
        
        // Handle errors
        if (result.error) {
          return handleAuthError(result.error, 'getSession');
        }
        
        // Success - reset error tracking
        if (result.data?.session) {
          consecutiveErrors = 0;
          rateLimitedUntil = 0;
          lastErrorTime = 0;
        }
        
        return result;
      } catch (error) {
        return handleAuthError(error, 'getSession exception');
      } finally {
        // Clear pending request after delay to prevent rapid retries
        setTimeout(() => {
          pendingSessionRequest = null;
        }, 1000);
      }
    })();
    
    return pendingSessionRequest;
  };
}

// Create wrapped getUser with request deduplication
function createSafeGetUser(originalGetUser: any) {
  return async function safeGetUser() {
    // Return pending request if one exists
    if (pendingUserRequest) {
      return pendingUserRequest;
    }
    
    // Check rate limiting
    if (isRateLimited() || shouldThrottleRequest()) {
      return { data: { user: null }, error: null };
    }
    
    // Create new request
    pendingUserRequest = (async () => {
      try {
        const result = await originalGetUser();
        
        // Handle errors
        if (result.error) {
          return handleAuthError(result.error, 'getUser');
        }
        
        return result;
      } catch (error) {
        return handleAuthError(error, 'getUser exception');
      } finally {
        // Clear pending request after delay
        setTimeout(() => {
          pendingUserRequest = null;
        }, 1000);
      }
    })();
    
    return pendingUserRequest;
  };
}

export function createClient(): SupabaseClient {
  // Return existing instance if available AND storage wasn't just cleared
  if (clientInstance && !storageJustCleared) {
    return clientInstance;
  }
  
  // Reset the flag
  const wasJustCleared = storageJustCleared;
  storageJustCleared = false;
  
  // CRITICAL: Run proactive token validation BEFORE client creation
  if (!wasJustCleared) {
    proactiveTokenValidation();
  }
  
  // Validate environment variables
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

  if (!supabaseUrl || !supabaseAnonKey || supabaseUrl.includes('undefined') || supabaseAnonKey.includes('undefined')) {
    throw new Error(
      'Supabase environment variables are not properly configured. Please ensure NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY are set.'
    );
  }

  // Validate stored session before creating client (skip if we just cleared)
  if (!wasJustCleared && !validateStoredSession()) {
    console.warn('[Auth] Invalid session detected, clearing storage');
    clearAuthStorage();
  }

  try {
    // Create client with proper configuration
    const client = createBrowserClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: !wasJustCleared, // NEW: Disable auto-refresh if we just cleared
        detectSessionInUrl: true,
        flowType: 'pkce',
        storage: {
          getItem: (key: string) => {
            if (typeof window === 'undefined') return null;
            return localStorage.getItem(key);
          },
          setItem: (key: string, value: string) => {
            if (typeof window === 'undefined') return;
            localStorage.setItem(key, value);
          },
          removeItem: (key: string) => {
            if (typeof window === 'undefined') return;
            localStorage.removeItem(key);
          },
        },
      },
      cookies: {
        getAll() {
          if (typeof document === 'undefined') return [];
          return document.cookie.split(';').map((cookie) => {
            const [name, ...rest] = cookie.trim().split('=');
            return { name, value: decodeURIComponent(rest.join('=')) };
          });
        },
        setAll(cookiesToSet: Array<{ name: string; value: string; options?: any }>) {
          if (typeof document === 'undefined') return;
          cookiesToSet.forEach(({ name, value, options }) => {
            let cookieString = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; Secure; SameSite=Lax`;

            if (options?.maxAge) {
              cookieString += `; max-age=${options.maxAge}`;
            }
            if (options?.domain) {
              cookieString += `; domain=${options.domain}`;
            }
            if (options?.expires) {
              cookieString += `; expires=${options.expires}`;
            }

            document.cookie = cookieString;
          });
        },
      },
    });

    // Wrap auth methods with error handling and request deduplication
    const originalGetSession = client.auth.getSession.bind(client.auth);
    const originalGetUser = client.auth.getUser.bind(client.auth);
    
    client.auth.getSession = createSafeGetSession(originalGetSession);
    client.auth.getUser = createSafeGetUser(originalGetUser);

    // Set up auth state change listener
    client.auth.onAuthStateChange(async (event, session) => {
      console.log('[Auth] State change:', event);
      
      // Reset error tracking on successful events
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session) {
          consecutiveErrors = 0;
          rateLimitedUntil = 0;
          lastErrorTime = 0;
        }
      }
      
      // Clear storage on sign out
      if (event === 'SIGNED_OUT') {
        clearAuthStorage();
      }
      
      // Handle token refresh failures
      if (event === 'TOKEN_REFRESHED' && !session) {
        console.warn('[Auth] Token refresh failed');
        clearAuthStorage();
      }
    });

    clientInstance = client;
    
    // NEW: Re-enable auto-refresh after client is created
    if (wasJustCleared) {
      setTimeout(() => {
        if (clientInstance) {
          console.log('[Auth] Re-enabling auto-refresh after cleanup');
          // The client will handle this automatically on next session check
        }
      }, 1000);
    }
    
    return client;
  } finally {
    isInitializing = false;
    initPromise = null;
  }
}

// Export client getter
export function getSupabaseClient(): SupabaseClient {
  return createClient();
}

export default createClient;

// Export convenience object
export const supabase = {
  get auth() {
    return createClient().auth;
  },
  get from() {
    return createClient().from.bind(createClient());
  },
  get channel() {
    return createClient().channel.bind(createClient());
  },
  get storage() {
    return createClient().storage;
  },
  get functions() {
    return createClient().functions;
  },
  get realtime() {
    return createClient().realtime;
  },
};